import { PageContextProps } from 'application/contexts/PageContext';
import Application from 'application/entities/Application';
import { AxiosRequestConfig } from 'axios';
import { ApiError } from 'helpers/apiError';
import { ApiRedirect } from 'helpers/apiRedirect';
import { axiosInstance as axios } from 'helpers/axiosInstance';
import getConfig from 'next/config';

export interface RequestInterface {
	pageContext?: Partial<PageContextProps>;
	url?: string;
	urlParams?: {
		[key: string]: string;
	};
	query?: string;
	startItem?: string;
	requestType?: 'pageContent' | 'dictionary' | 'siblingNavigation' | 'site' | 'sitemap' | 'shortUrls';
	globalApiEnabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const queryCDA = async <TResponse = any>({
	pageContext,
	url = '/',
	urlParams,
	startItem,
	requestType,
	globalApiEnabled = false,
}: RequestInterface): Promise<TResponse> => {
	const { preview } = pageContext ?? {};
	const { publicRuntimeConfig } = getConfig() ?? {};

	const { APP_ENV, CONTENT_API_URL: contentApi, GLOBAL_API_URL: globalApi } = publicRuntimeConfig ?? {};

	if (!contentApi) throw new ApiError('CONTENT_API_URL:NOTDEFINED', 500, 'Content api url is not defined');
	if (!process.env.CDA_KEY) throw new ApiError('CDA_KEY:NOTDEFINED', 500, 'Content delivery api key is not defined');

	try {
		const options: AxiosRequestConfig = {
			url: (globalApiEnabled ? globalApi : contentApi) + encodeURI(decodeURIComponent(url)),
			params: urlParams,
			method: 'GET',
			maxRedirects: 0,
			headers: {
				ContentType: 'application/json',
				'Accept-Language': Application.locale?.baseName ?? pageContext?.content?.subject?.culture,
				'Api-Key': process.env.CDA_KEY,
				Vary: 'Accept-Language',
				Preview: preview ?? false,
				Hostname: pageContext.host,
			},
		};
		options.headers['Start-item'] = startItem;
		const response = await axios(options);

		const { data } = response;

		return data;
	} catch (error) {
		const message = error?.response?.data?.message ?? error;
		const statusCode = error?.response?.status || 500;
		const responseErrors = error?.cause?.response?.data?.errors;
		const numberOfErrors = responseErrors?.length;
		switch (statusCode) {
			case 301:
			case 302:
			case 307:
			case 308:
				throw new ApiRedirect(statusCode, error?.response?.headers?.location);
			case 404:
				throw new ApiError('PageNotFound', 404, 'Page not found');
			case 401: // "401 Unauthorized" really means "unauthenticated"
			case 403: // "403 Forbidden" really means "unauthorized"
				return error?.response?.data?.data;
			default:
				APP_ENV != 'prod' && console.log('error cause:', error.cause);
				throw new ApiError(
					'Could not grab content from CMS',
					statusCode,
					message +
						` Number of errors: ${numberOfErrors}. Query/RequestType: ${requestType ? requestType : error?.cause?.query}. Url: ${url}`,
				);
		}
	}
};
